import RadioInput from '@repo/ui/radio-input'
import SelectInput from '@repo/ui/select-input'
import Typography from '@repo/ui/typography'
import { useQuery } from '@tanstack/react-query'

import { getCenterJourneys } from '../../../../api/centers/get-center-journeys'
import { getCenterTrips } from '../../../../api/centers/get-center-trips'
import {
  MedicalConditionEnum,
  medicalConditionEnumMapping
} from '../../../../lib/types/enums/enrollment/medical-condition-enum'
import { CenterToJourney } from '../../../../lib/types/models/center-to-journey/center-to-journey'
import { CenterToJourneyProperties } from '../../../../lib/types/models/center-to-journey/properties.center-to-journey'
import { EnrollmentFilter } from '../../../../lib/types/models/enrollment/enrollment-filter'
import { TripProperties } from '../../../../lib/types/models/trip/properties.trip'
import { getSelectedOption } from '../../../../lib/utils/functions/misc.functions'

interface Props {
  centerId: number
  filter?: EnrollmentFilter
  onFilterChange: (filter: EnrollmentFilter) => void
}

function getJourneyLabel(centerJourney: CenterToJourney) {
  const options = CenterToJourneyProperties.getOptions(centerJourney)

  return [
    centerJourney.journey.reference,
    CenterToJourneyProperties.getThemeName(centerJourney),
    ...(options.length ? [options.join(', ')] : [])
  ].join(' - ')
}

export function CenterEnrollmentsFilter({ centerId, filter, onFilterChange }: Props) {
  function setFilter<K extends keyof EnrollmentFilter>(key: K, value: EnrollmentFilter[K]) {
    onFilterChange({ ...filter, [key]: value })
  }

  const { data: centerJourneys } = useQuery({
    queryKey: ['center-journeys', centerId],
    queryFn: () => getCenterJourneys(centerId)
  })

  const { data: trips } = useQuery({
    queryKey: ['center-trips', centerId],
    queryFn: () => getCenterTrips(centerId)
  })

  const selectedCenterJourney = centerJourneys?.find(
    (centerJourney) => centerJourney.journey.id === filter?.journeyId
  )

  const secondaryTrips = centerJourneys
    ?.map((centerJourney) => CenterToJourneyProperties.getSecondaryTripsData(centerJourney))
    .flat()
    .filter((trip, index, self) => index === self.findIndex((t) => t?.id === trip?.id))

  return (
    <div className={'flex flex-col gap-3'}>
      <div />
      <SelectInput
        label={'Séjour'}
        options={
          centerJourneys?.map((centerJourney) => ({
            label: getJourneyLabel(centerJourney),
            value: centerJourney.journey.id
          })) ?? []
        }
        searchable
        selectedOption={getSelectedOption(
          filter?.journeyId,
          selectedCenterJourney && getJourneyLabel(selectedCenterJourney)
        )}
        onChange={(journeyId) => setFilter('journeyId', journeyId)}
      />

      <SelectInput
        label={'Voyage principal'}
        options={
          trips?.map((trip) => ({
            label: TripProperties.getTripLabel(trip),
            value: trip.id
          })) ?? []
        }
        searchable
        selectedOption={getSelectedOption(
          filter?.mainTripId,
          TripProperties.getTripLabel(trips?.find((trip) => trip.id === filter?.mainTripId))
        )}
        onChange={(mainTripId) => setFilter('mainTripId', mainTripId)}
      />

      <SelectInput
        label={'Voyage secondaire'}
        options={
          secondaryTrips?.map((trip) => ({
            label: TripProperties.getTripLabel(trip),
            value: trip?.id
          })) ?? []
        }
        selectedOption={getSelectedOption(
          filter?.secondaryTripId,
          TripProperties.getTripLabel(
            secondaryTrips?.find((trip) => trip?.id === filter?.secondaryTripId)
          )
        )}
        onChange={(trip) => setFilter('secondaryTripId', trip)}
      />

      <SelectInput
        label={'Niveau de santé'}
        options={Object.values(MedicalConditionEnum).map((value) => ({
          label: medicalConditionEnumMapping[value].label,
          value
        }))}
        selectedOption={getSelectedOption(
          filter?.medicalLevel,
          filter?.medicalLevel ? medicalConditionEnumMapping[filter.medicalLevel].label : undefined
        )}
        onChange={(medicalLevel) => setFilter('medicalLevel', medicalLevel)}
      />

      <div className={'flex flex-col gap-2'}>
        <Typography variant={'label-semibold'}>Placement en famille</Typography>

        <div className="flex flex-row gap-5">
          <RadioInput
            label={'Tous'}
            checked={filter?.familyPlacements === undefined}
            onCheck={() => setFilter('familyPlacements', undefined)}
          />
          <RadioInput
            label={'Placé en famille'}
            checked={filter?.familyPlacements === true}
            onCheck={() => setFilter('familyPlacements', true)}
          />

          <RadioInput
            label={'Non placé en famille'}
            checked={filter?.familyPlacements === false}
            onCheck={() => setFilter('familyPlacements', false)}
          />
        </div>
      </div>

      <div className={'flex flex-col gap-2'}>
        <Typography variant={'label-semibold'}>Documents complétés</Typography>

        <div className="flex flex-row gap-5">
          <RadioInput
            label={'Tous'}
            checked={filter?.documentsCompleted === undefined}
            onCheck={() => setFilter('documentsCompleted', undefined)}
          />
          <RadioInput
            label={'Tous complétés'}
            checked={filter?.documentsCompleted === true}
            onCheck={() => setFilter('documentsCompleted', true)}
          />

          <RadioInput
            label={'Non complétés'}
            checked={filter?.documentsCompleted === false}
            onCheck={() => setFilter('documentsCompleted', false)}
          />
        </div>
      </div>
    </div>
  )
}
